import type { BuildingRules } from '@full-elevator/sim';
import { entrancesOf } from '@full-elevator/sim';
import { groundOf } from '../floorNames.js';
import type { RoomKind } from '../ui/homeModel.js';

/**
 * Which room each floor of a level is, in play (ADR-0018 steps 3–4).
 *
 * A floor in play is a storey of the building, not a shift, so this is its own
 * mapping — but the rooms are the same drawings the home screen uses. Picked by
 * the floor's place in the building and the building's rules, never per level,
 * and each building's rule gets the room that shows it: a train platform on
 * the Transit Centre's second way in — underground, since ADR-0028, two floors
 * under the street with the ticket hall between — a sky lobby on Skyline Tower's transfer
 * floor, and a reception — the one floor with no landing limit — at the foot
 * of the hotel, whose other floors are corridors with exactly as many rugs as
 * guests may stand on them (drawn by the shaft view, where the row really is).
 */
const MIDDLE: Readonly<Record<string, readonly RoomKind[]>> = {
  'office-tower': ['office-desks', 'office-meeting', 'office-desks', 'office-cafeteria'],
  'grand-hotel': ['hotel-corridor', 'hotel-housekeeping', 'hotel-corridor', 'hotel-luggage'],
  // Above the street: the offices over the station. Its own rooms are below.
  'transit-centre': ['office-desks', 'office-meeting', 'office-cafeteria', 'office-desks'],
  'skyline-tower': ['skyline-office', 'skyline-office', 'office-meeting', 'skyline-office'],
};

const GROUND: Readonly<Record<string, RoomKind>> = {
  'office-tower': 'office-lobby',
  'grand-hotel': 'hotel-reception',
  'transit-centre': 'transit-tickets',
  'skyline-tower': 'office-lobby',
};

const TOP: Readonly<Record<string, RoomKind>> = {
  'office-tower': 'office-executive',
  // Breakfast is served at the top (ADR-0051): the guests running for it go up.
  'grand-hotel': 'hotel-restaurant',
  'transit-centre': 'office-executive',
  'skyline-tower': 'skyline-view',
};

export function roomForFloor(
  buildingId: string | undefined,
  rules: BuildingRules,
  floor: number,
  floors: number,
): RoomKind {
  const id = buildingId ?? 'office-tower';
  const ground = groundOf(buildingId);
  if (rules.interchange && floor === rules.interchange.floor) return 'skyline-skylobby';
  if (floor === ground) return GROUND[id] ?? 'office-lobby';
  // A second way in is a train platform: the Transit Centre's rule, as a place.
  // Under the street it is the metro, with the hall the trains empty into above it.
  const door = entrancesOf(rules).includes(floor);
  if (floor < ground) return door ? 'transit-platform' : 'transit-concourse';
  if (door) return 'transit-platform';
  if (floor === floors - 1) return TOP[id] ?? 'office-executive';
  const middle = MIDDLE[id] ?? MIDDLE['office-tower'] ?? ['office-desks'];
  return middle[(floor - ground - 1) % middle.length] as RoomKind;
}

/**
 * How many rugs a floor's landing has: the building's landing limit, except on
 * an entrance, where the limit does not apply. Zero means no rugs at all.
 */
export function rugsOn(rules: BuildingRules, floor: number): number {
  if (rules.landingCapacity === undefined) return 0;
  return entrancesOf(rules).includes(floor) ? 0 : rules.landingCapacity;
}
